/**
 * Generating activation codes, and reducing a typed one to what is compared.
 *
 * The alphabet leaves out 0/O and 1/I/L. These codes get read down a phone to
 * someone in another city, and "sıfır mı O mu" is a question that cannot be
 * asked if only one of the pair exists. What is left is 32 characters, which
 * is also why the code is generated in 5-bit chunks.
 */

const ALPHABET = '23456789ABCDEFGHJKMNPQRSTUVWXYZ'

/**
 * Codes are matched on their canonical form: dashes and spaces are grouping
 * for the eye, and case is never allowed to decide whether a code works.
 * functions/activate has the same function, the two must agree exactly or a
 * code that was generated here will not hash to the same value there.
 */
export function canonicalCode(code: string): string {
  return code.toUpperCase().replace(/[^0-9A-Z]/g, '')
}

/** Groups of four, which is as much as anyone reads back without losing place. */
export function formatCode(prefix: string, body: string): string {
  const groups = body.match(/.{1,4}/g) ?? []
  return `${prefix}-${groups.join('-')}`
}

/**
 * A new code: a prefix naming what it opens, then eight random characters.
 *
 * 32^8 is over a trillion, so guessing is not a way in, but the server rate
 * limits attempts anyway, because "very unlikely" is not a reason to leave a
 * door unlatched.
 */
export function generateCode(prefix = 'KRM'): string {
  const bytes = new Uint8Array(8)
  crypto.getRandomValues(bytes)
  // Modulo over 32 with a 31-character alphabet is deliberate: the bias is
  // one extra chance in 256 for a single character, which is nothing against
  // a trillion-code space, and the alternative is rejection sampling for no
  // gain anyone could measure.
  const body = Array.from(bytes, (b) => ALPHABET[b % ALPHABET.length]).join('')
  return formatCode(prefix, body)
}

/** SHA-256, hex. The only form of a code that is ever stored. */
export async function hashCode(code: string): Promise<string> {
  const bytes = new TextEncoder().encode(canonicalCode(code))
  const digest = await crypto.subtle.digest('SHA-256', bytes)
  return Array.from(new Uint8Array(digest))
    .map((b) => b.toString(16).padStart(2, '0'))
    .join('')
}

/**
 * What the panel shows instead of the code it can no longer see: the prefix
 * and the first group. Enough to tell two codes apart in a list, nowhere near
 * enough to use one.
 */
export function labelFor(code: string): string {
  const [prefix, first] = code.split('-')
  return `${prefix}-${first}-••••`
}

/**
 * PINs that are not worth having. Refused when an institution appoints its
 * own first owner, left to itself every one of them would be 1234.
 *
 * functions/activate has the same list and enforces it server-side; this copy
 * is so the person choosing hears about it while they are still typing.
 */
export function isWeakPin(pin: string): boolean {
  if (/^(\d)\1+$/.test(pin)) return true
  if ('0123456789'.includes(pin) || '9876543210'.includes(pin)) return true
  return ['1234', '12345', '123456', '1122', '2580', '0852'].includes(pin)
}
